// The Chapter header: which Chapter, what state it is in, what Refinement did
// to it, which version is on screen, and how to read it.

import type { ChapterReview } from "@contract";
import { memo } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "../../../components/control/Button";
import { SegmentedControl } from "../../../components/control/SegmentedControl";
import { StateChip } from "../../../components/control/StateChip";
import { StatLine } from "../../../components/data/StatLine";
import { VIEW_MODES, type ViewMode } from "./viewMode";

export const ChapterHeader = memo(function ChapterHeader({
  chapter,
  mode,
  onModeChange,
  onOpenVersions,
}: {
  chapter: ChapterReview;
  mode: ViewMode;
  onModeChange: (mode: ViewMode) => void;
  onOpenVersions: () => void;
}) {
  const { t } = useTranslation("review");
  const script = chapter.script;
  // What Refinement did to this version. The word counts always show, because
  // "+0 -0" is itself the answer to "did Refinement touch this?"; the rest
  // appear only when there is something to report, so the German line stays
  // inside the header at 1,280 pixels. The calls are spelled out here rather
  // than in a helper that takes `t`, so the reference scan can see them.
  const stats = script?.stats;
  const statItems: string[] = [];
  if (stats) {
    statItems.push(t("stats.diff", { added: stats.wordsAdded, removed: stats.wordsRemoved }));
    if (stats.footnotes > 0) statItems.push(t("stats.footnotes", { count: stats.footnotes }));
    if (stats.figures > 0) statItems.push(t("stats.figures", { count: stats.figures }));
    if (stats.furnitureRemoved > 0) {
      statItems.push(t("stats.furnitureRemoved", { count: stats.furnitureRemoved }));
    }
    if (stats.tones > 0) statItems.push(t("stats.tones", { count: stats.tones }));
    if (stats.droppedNodes > 0) statItems.push(t("stats.dropped", { count: stats.droppedNodes }));
  }
  const options = VIEW_MODES.map((value) => ({
    value,
    label:
      value === "sideBySide"
        ? t("view.sideBySide")
        : value === "inline"
          ? t("view.inline")
          : t("view.scriptOnly"),
  }));

  return (
    <header className="flex flex-col gap-1.5 px-4 md:px-9 pt-4 pb-3">
      {/* Title and controls on one line, the statistics under them. The
          mockup draws all four on a single row, which holds for its own short
          title; a long one wrapped the version control and the view toggle
          onto a second line at the left, so the two controls that have to be
          in the same place on every Chapter moved between Chapters. They are
          `shrink-0` and never wrap now, and the title takes what is left. */}
      <div className="flex min-w-0 flex-wrap items-baseline gap-2 md:gap-4">
        <h1 className="m-0 min-w-0 font-serif text-title-lg font-semibold text-ink">
          {t("heading", { number: chapter.number ?? chapter.position, title: chapter.title })}
        </h1>
        {/* The Chapter's own state, where the Author is reading. Without it an
            edit of an Approved Chapter takes the approval back and the main
            column says nothing about it. */}
        <StateChip kind="chapter" state={chapter.state} className="shrink-0" />
        <span className="flex-1" />
        <div className="flex min-w-0 flex-wrap items-baseline gap-2 md:gap-4">
          {chapter.versions.length > 0 ? (
            <Button variant="quiet" size="sm" onClick={onOpenVersions}>
              {t("version.control", {
                number: script?.version.number ?? chapter.versions.length,
                total: chapter.versions.length,
              })}
            </Button>
          ) : null}
          <SegmentedControl
            appearance="text"
            label={t("view.label")}
            options={options}
            value={mode}
            onChange={onModeChange}
          />
        </div>
      </div>
      {stats ? <StatLine items={statItems} /> : null}
    </header>
  );
});
